import { defineStore } from "pinia";
import { useSessionStore } from "./session.store";
import type { UserV1 } from "@territoriesai/types";

export enum FeatureFlagId {
  TERRITORY_MODELS = "territory-models",
  ACCOUNT_FIND = "account-find",
  CAPACITY = "capacity",
  SALESFORCE_INTEGRATION = "salesforce-integration"
}

export interface FeatureFlag {
  id: FeatureFlagId;
  name: string;
  description: string;
  enabled: boolean;
  enabledForEmails: string[];
}

export const useFeatureFlagStore = defineStore("featureFlags", {
  state: () => ({
    flags: [] as FeatureFlag[],
    isLoaded: false
  }),
  actions: {
    async loadFeatureFlags(): Promise<void> {
      if (this.isLoaded) return;

      try {
        this.flags = this.loadFlagsFromEnvironment();
        this.isLoaded = true;
      } catch (error) {
        console.error(
          "TerritoriesAI: Error loading feature flags. Error [" + error + "]"
        );
        // Don't set isLoaded=true on error so flags can be retried
      }
    },
    loadFlagsFromEnvironment(): FeatureFlag[] {
      // when explicitly disabled, features can be enabled for specific emails
      const enabledForEmails: string[] = (
        import.meta.env.VITE_TAI_FEATURE_EMAILS || ""
      )
        .split(",")
        .filter(Boolean)
        .map((id: string) => id.trim());

      // assume all features are enabled by default, unless explicitly disabled
      const disabledFeatures: FeatureFlagId[] = (
        import.meta.env.VITE_TAI_DISABLED_FEATURES || ""
      )
        .split(",")
        .filter(Boolean)
        .map((id: string) => id.trim());

      const features: FeatureFlag[] = [
        {
          id: FeatureFlagId.TERRITORY_MODELS,
          name: "Territories",
          description: "Territories management feature",
          enabled: !disabledFeatures.includes(FeatureFlagId.TERRITORY_MODELS),
          enabledForEmails
        },
        {
          id: FeatureFlagId.ACCOUNT_FIND,
          name: "Account Find",
          description: "Account find feature",
          enabled: !disabledFeatures.includes(FeatureFlagId.ACCOUNT_FIND),
          enabledForEmails
        },
        {
          id: FeatureFlagId.CAPACITY,
          name: "Capacity",
          description: "Capacity feature",
          enabled: !disabledFeatures.includes(FeatureFlagId.CAPACITY),
          enabledForEmails
        },
        {
          id: FeatureFlagId.SALESFORCE_INTEGRATION,
          name: "Salesforce Integration",
          description: "Salesforce integration feature",
          enabled: !disabledFeatures.includes(
            FeatureFlagId.SALESFORCE_INTEGRATION
          ),
          enabledForEmails
        }
      ];

      return features;
    },
    // Map of routes to required feature flags
    getRouteFeatureMap(): Record<string, FeatureFlagId> {
      return {
        "/territory-model": FeatureFlagId.TERRITORY_MODELS,
        "/capacity": FeatureFlagId.CAPACITY
      };
    },
    // Check if feature is enabled
    isFeatureEnabled(featureId: FeatureFlagId): boolean {
      const sessionStore = useSessionStore();
      const sessionUser = sessionStore.selectedUser();
      if (!sessionUser) return false;

      return this.isFeatureEnabledForSession(featureId, sessionUser);
    },
    // Check if feature is enabled for a specific session
    isFeatureEnabledForSession(
      featureId: FeatureFlagId,
      sessionUser: UserV1
    ): boolean {
      const feature = this.flags.find((f) => f.id === featureId);
      //disabled by default, unless explicitly enabled
      if (!feature) return false;

      // explicitly enabled overrides email
      if (feature.enabled) return true;

      // Check if specific email is enabled
      const userEmail = sessionUser.email?.toLowerCase();
      if (
        userEmail &&
        feature.enabledForEmails?.some(
          (email) => email.toLowerCase() === userEmail
        )
      )
        return true;

      return false;
    }
  }
});
